import { useEffect, useMemo } from 'react';
import { Card, Box, Typography, Button, CardMedia, FormControlLabel, Switch } from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import moment from 'moment';
import 'moment/locale/es';

import { urlImg, ColorButton } from '../../common/helpers';

import { useSelector, useDispatch } from 'react-redux';
import { updateProfilePageStatus } from '../../../actions/profile';

const PageCard = ({ page, type, onEdit, onDelete, onView }) => {
  const categories = useSelector(state => state.cat.categories);
  const isDraft = type === 'DRAFT';
  const dateLabel = isDraft ? 'Creado el' : 'Publicado el';
  const dateValue = moment(page.date).format('LL');

  const categoriaName = useMemo(() => {
    if (!page?.categoria) return null;
    // si la propiedad ya viene como objeto con name
    if (typeof page.categoria === 'object') return page.categoria.name || null;
    // si viene como id (string) buscar en el store
    const found = categories?.find(cat => cat._id === page.categoria);
    return found ? found.name : page.categoria;
  }, [page?.categoria, categories]);

  const dispatch = useDispatch();

  const cambiarStatus = async status => {
    await dispatch(updateProfilePageStatus({ status }, page._id));
  };

  return (
    <Card className="card-edit" sx={{ height: 200, overflow: 'hidden' }}>
      <CardMedia
        component="img"
        className='picture-item'
        sx={{ width: 140, alignSelf: 'stretch', objectFit: 'cover' }}
        image={page.img && page.img.length > 0 ? `${urlImg}/paginas/${page._id}/${page.img}` : '/search.png'}
        alt={page.nombre}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, m: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="h6" fontWeight="bold" color="primary">
                {page.nombre || 'Sin título'}
              </Typography>
              <Box 
                sx={{ 
                  px: 1, 
                  py: 0.2, 
                  borderRadius: '4px', 
                  bgcolor: (page.miembros || []).length > 1 ? '#E6FFFA' : '#EBF8FF',
                  color: (page.miembros || []).length > 1 ? '#2C7A7B' : '#2B6CB0',
                  fontSize: '0.7rem',
                  fontWeight: 'bold',
                  textTransform: 'uppercase'
                }}
              >
                {(page.miembros || []).length > 1 ? 'Grupo / Negocio' : 'Independiente'}
              </Box>
            </Box>
            <Typography variant="body2" sx={{
              mt: 1, mb: 2,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}>
              {page.descripcion || 'Sin descripción'}
            </Typography>

            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', mb: 2 }}>
              {/* Duration - Mocked for now as it wasn't in the form explicitly, or maybe derived */}
              {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessTimeIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">Duración: 3 horas</Typography>
              </Box> */}

              {/* {page.ubicacion && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationOnIcon fontSize="small" color="action" />
                  <Typography variant="caption">{page.ubicacion}</Typography>
                </Box>
              )} */}

              {page.categoria && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocalOfferIcon fontSize="small" color="action" />
                  <Typography variant="caption">{categoriaName}</Typography>
                </Box>
              )}
            </Box>
          </Box>

          {!isDraft && (
            <Box sx={{ textAlign: 'center', m: 2 }}>
              <Box sx={{
                width: 60, height: 60, display: 'flex', flexDirection: 'column',
                justifyContent: 'center', alignItems: 'center'
              }}>
                <Typography variant="h4" fontWeight="bold" lineHeight={1}>
                  {page.servicios ? page.servicios.length : 0}
                </Typography>
                <Typography variant='body2'>
                  Servicios disponibles
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
          <Typography variant="caption">
            {dateLabel} {dateValue}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {!isDraft && (
              <Box className="toggle" sx={{ display: 'flex', alignItems: 'center', marginRight: 1 }}>
                <Typography variant="body2" color="#737791" sx={{ marginRight: 1 }}>
                  {page?.status ? "Activo" : "Inactivo"}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={page?.status || false}
                      onChange={(e) => cambiarStatus(e.target.checked)}
                      name="status"
                      size="small"
                    />
                  }
                  label=""
                  sx={{ m: 0 }}
                />
              </Box>
            )}
            <Button
              variant="contained"
              color="success"
              size="small"
              onClick={onView}
              sx={{ color: "#FFF", minWidth: "auto", marginRight: "0.5rem", fontSize: "14px", textTransform: "none" }}
              endIcon={<VisibilityIcon fontSize="small" />}
            >
              Ver
            </Button>
            <ColorButton
              variant="contained"
              color="primary"
              size="small"
              onClick={onEdit}
              sx={{ color: "#FFF", minWidth: "auto", marginRight: "0.5rem", fontSize: "14px", textTransform: "none" }}
              endIcon={<EditIcon fontSize="small" />}
            >
              Editar
            </ColorButton>
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={onDelete}
              sx={{ color: "#FFF", minWidth: "auto", marginRight: "0.5rem", fontSize: "14px", textTransform: "none" }}
              endIcon={<DeleteIcon fontSize="small" />}
            >
              Eliminar
            </Button>
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

export default PageCard;
